import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import useT from '@/lib/t';

import { areaFormatter, lotSizeTitleLangKeyFormatter, unitAreaLabelFormatter } from '@/src/libs/number-helper';
import { isSet } from '@/utils/formatChecker';

import ConditionalFragment from '@/components/common/ConditionalFragment';
import { amenitiesFormatter } from '@/src/libs/special-helper';
import {
  countParkingSpace,
  getBuildingAge,
  getFloorInformation,
  getParkingDetail,
  totalSizeFormatter,
} from '@/utils/dataConvertor';
import { IListingInBuyPage } from './HouseDetailContainerProps';

const MainData = ({ detailData }: { detailData: IListingInBuyPage }) => {
  const t = useT('listing');

  const parkingSize = Number.isNaN(parseFloat(detailData.detail_parking_size ?? ''))
    ? undefined
    : detailData.detail_parking_size;

  return (
    <div className={'pt-7'}>
      <section id='house-detail' className='text-lg leading-loose font-medium text-dark-grey uppercase mb-2'>
        詳細資料
      </section>
      <div className={'flex flex-col sm:flex-row'}>
        <MainDataItemContainer>
          {detailData.detail_purpose && (
            <MainDataItemDescription isSubItem={false} name={t('detail_purpose')} value={detailData.detail_purpose} />
          )}
          {detailData.detail_category && (
            <MainDataItemDescription isSubItem={false} name={t('detail_category')} value={detailData.detail_category} />
          )}
          {detailData.detail_floor && (
            <MainDataItemDescription
              isSubItem={false}
              name={t('detail_floor')}
              value={getFloorInformation(detailData)}
            />
          )}
          {detailData.detail_age && (
            <MainDataItemDescription isSubItem={false} name={t('detail_age')} value={getBuildingAge(detailData)} />
          )}
          <MainDataItemDescription
            isSubItem={false}
            name={t('detail_guard_management')}
            value={detailData.detail_guard_management ? '有' : '無'}
          />
          {detailData.detail_amenities && (
            <MainDataItemDescription
              alignments={'start'}
              isSubItem={false}
              name={t('detail_amenities')}
              value={detailData.detail_amenities}
            />
          )}
        </MainDataItemContainer>
        <MainDataItemContainer>
          {detailData.detail_total_area_size && (
            <MainDataItemDescription
              name={t('detail_total_area_size')}
              value={totalSizeFormatter(detailData.detail_total_area_size, detailData.country!)}
              unit={''}
            />
          )}
          <ConditionalFragment
            condition={
              isSet(detailData.detail_main_area_size) ||
              isSet(detailData.detail_other_area_size) ||
              isSet(detailData.detail_amenities_area) ||
              isSet(parkingSize)
            }
          >
            <CollapsibleData
              title={t('house_detail_total_area_size')}
              titleValue={areaFormatter(detailData.detail_total_area_size, {
                omit: [parkingSize!],
                country: detailData.country!,
              })}
              titleUnit={unitAreaLabelFormatter(detailData.country!)}
            >
              {detailData.detail_main_area_size && (
                <MainDataItemDescription
                  isSubText
                  isSubItem={true}
                  name={t('detail_main_area_size')}
                  value={areaFormatter(detailData.detail_main_area_size, { country: detailData.country! })}
                  unit={unitAreaLabelFormatter(detailData.country!)}
                />
              )}
              {detailData.detail_other_area_size && (
                <MainDataItemDescription
                  isSubText
                  isSubItem={true}
                  name={t('detail_other_area_size')}
                  value={areaFormatter(detailData.detail_other_area_size, { country: detailData.country! })}
                  unit={unitAreaLabelFormatter(detailData.country!)}
                />
              )}
              {detailData.detail_amenities_area && (
                <MainDataItemDescription
                  isSubText
                  isSubItem={true}
                  name={`${t('detail_amenities_area')}${amenitiesFormatter(detailData?.is_parking_space_in_amenities)}`}
                  value={areaFormatter(detailData.detail_amenities_area, { country: detailData.country! })}
                  unit={unitAreaLabelFormatter(detailData.country!)}
                  endWithBorder={isSet(parkingSize) && !detailData.is_parking_space_in_amenities}
                />
              )}
            </CollapsibleData>
            {isSet(parkingSize) && !detailData.is_parking_space_in_amenities && (
              <MainDataItemDescription
                isSubItem={true}
                name={t('detail_parking_size')}
                value={areaFormatter(parkingSize!, { country: detailData.country! })}
                unit={unitAreaLabelFormatter(detailData.country!)}
              />
            )}
          </ConditionalFragment>
          {detailData.detail_lot_size && (
            <MainDataItemDescription
              isSubItem={false}
              name={t(lotSizeTitleLangKeyFormatter(detailData.detail_category))}
              value={areaFormatter(detailData.detail_lot_size, { country: detailData.country! })}
              unit={unitAreaLabelFormatter(detailData.country!)}
            />
          )}
          {getParkingDetail(detailData)[0] !== '-' &&
            getParkingDetail(detailData).map((detail, index, array) => (
              <React.Fragment key={index}>
                <ConditionalFragment condition={array.length > 1 && index === 0}>
                  <MainDataItemDescription
                    isSubItem={false}
                    name={index === 0 ? t('detail_parking') : ''}
                    value={countParkingSpace(detailData)}
                  />
                </ConditionalFragment>
                <MainDataItemDescription
                  isSubItem={array.length > 1}
                  name={array.length > 1 ? '' : t('detail_parking')}
                  value={detail === 'false' ? '無' : detail === 'true' ? '有' : detail}
                />
              </React.Fragment>
            ))}
          {detailData.detail_management_fee && (
            <MainDataItemDescription
              isSubItem={false}
              name={t('detail_management_fee')}
              value={detailData.detail_management_fee}
            />
          )}
        </MainDataItemContainer>
      </div>
    </div>
  );
};

const MainDataItemContainer: React.FC = ({ children }) => {
  return (
    <div className={'flex-1 flex-shrink-0 items-stretch flex flex-col flex-nowrap sm:mr-8 sm:last:mr-0'}>
      {children}
    </div>
  );
};

const MainDataItemDescription = ({
  name,
  value,
  isSubItem,
  isSubText,
  unit,
  alignments = 'center',
  endWithBorder,
}: {
  name: string;
  value?: string | null | number;
  isSubItem?: boolean;
  isSubText?: boolean;
  unit?: string;
  alignments?: 'center' | 'start';
  endWithBorder?: boolean;
}) => {
  return (
    <div
      className={classNames(
        `w-full pr-4`,
        {
          'bg-gray50 pl-xl': isSubItem,
          'bg-white mb-2': !isSubItem,
        },
        {
          'text-gray700': isSubText,
          'text-gray900': !isSubText,
        },
        {
          'items-center': alignments === 'center',
          'items-start': alignments === 'start',
        }
      )}
    >
      <div
        className={classNames('flex flex-row flex-nowrap justify-between', 'py-2 ', {
          'border-b border-solid': endWithBorder,
        })}
      >
        <p
          className={classNames(
            'flex-shrink-0',
            { 'w-1/2 max-w-[7.5rem]': !isSubItem, 'w-auto': isSubItem },
            'leading-normal tracking-wide uppercase'
          )}
        >
          {name}
        </p>
        <ConditionalFragment condition={isSet(value)}>
          <div
            className={classNames(
              'min-w-1/2 flex-1 flex-shrink-0',
              'leading-normal tracking-wide text-dark-grey uppercase text-right'
            )}
          >
            {`${value} ${unit ? unit : ''}`.split('\n').map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </ConditionalFragment>
      </div>
    </div>
  );
};

const CollapsibleData: React.FC<{
  title: string;
  titleValue: string | null | number;
  titleUnit: string;
}> = ({ title, titleValue, titleUnit, children }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const collapsibleRef = useRef<HTMLDivElement>(null);

  const displayText = useMemo(() => {
    if (isExpanded) return `隱藏細節`;
    else return `查看細節`;
  }, [isExpanded, title]);

  const height = useMemo(() => {
    if (isExpanded) return collapsibleRef.current?.scrollHeight || 0;
    else return 0;
  }, [isExpanded]);

  return (
    <div>
      <div
        className={classNames(
          'flex flex-row flex-nowrap',
          'pl-xl py-2 pr-4',
          'w-full',
          'bg-gray50',
          'text-gray900',
          'cursor-pointer'
        )}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className={'flex items-center mr-2'}>
          <span className={'flex-1 truncate'}>{title}</span> (<p className={'underline'}>{displayText}</p>)
        </div>
        <div className={'ml-auto'}>
          {titleValue}
          {titleUnit}
        </div>
      </div>
      <div
        ref={collapsibleRef}
        style={{ height, transition: 'height', transitionDuration: '300ms' }}
        className={classNames('overflow-hidden')}
      >
        {children}
      </div>
    </div>
  );
};

export default MainData;
